import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Invoice } from 'src/app/shared/models/invoice.interface';

@Component({
  selector: 'app-dummy-invoice',
  templateUrl: './dummy-invoice.component.html',
  styleUrls: ['./dummy-invoice.component.scss']
})
export class DummyInvoiceComponent implements OnInit, OnChanges {
  @Input() invoices: Invoice[] = [];
  @Input() viewMode: string = 'individual';
  @Output() editableDataChange = new EventEmitter<any>();

  editableInvoices: any[] = [];
  hasChanges: boolean = false;

  constructor() { }

  ngOnInit(): void {
    this.initializeEditableData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only re-initialize if invoices actually changed (not just reference)
    if (changes['invoices']) {
      const currentValue = changes['invoices'].currentValue;
      const previousValue = changes['invoices'].previousValue;

      // Check if it's a real change, not just a reference change
      if (!previousValue || currentValue !== previousValue) {
        this.initializeEditableData();
        this.hasChanges = false;
      }
    }

    // Re-initialize if view mode changes
    if (changes['viewMode'] && !changes['viewMode'].firstChange) {
      this.initializeEditableData();
      this.hasChanges = false;
    }
  }

  initializeEditableData(): void {
    this.editableInvoices = this.invoices.map(invoice => ({
      ...invoice,
      editableVehicle: invoice.vehicle ? {
        make: invoice.vehicle.company_name || '',
        model: invoice.vehicle.name || '',
        year: invoice.vehicle.year || '',
        body_type: invoice.vehicle.body_type || '',
        engine: invoice.vehicle.engine || '',
        mileage: invoice.vehicle.mileage || '',
        stock_no: invoice.vehicle.car_no || '',
        fuel_type: invoice.vehicle.fuel_type || '',
        exterior_color: invoice.vehicle.exterior_color || '',
        transmission: invoice.vehicle.transmission || '',
        car_weight: invoice.vehicle.car_weight || '',
        chassis_no: invoice.vehicle.vin_no || '',
        pictures: invoice.vehicle.pictures || []
      } : null,
      editableVehicles: invoice.vehicles ? invoice.vehicles.map(v => ({
        ...v,
        make: v.company_name || '',
        model: v.name || '',
        year: v.year || '',
        chassis_no: v.vin_no || '',
        stock_no: v.car_no || '',
        engine: v.engine || '',
        mileage: v.mileage || '',
        fuel_type: v.fuel_type || '',
        exterior_color: v.exterior_color || '',
        transmission: v.transmission || '',
        invoice_number: v.invoice_number || '',
        invoice_amount: v.invoice_amount || 0
      })) : null,
      editableInvoiceAmount: invoice.invoice_amount || 0,
      // Editable shipping and invoice details
      editablePortOfLoading: 'ANY PORT IN JAPAN',
      editablePortOfDischarging: `${invoice.order?.to_port || ''}, ${invoice.order?.to_country || ''}`.trim().replace(/^,\s*/, ''),
      editableBankName: 'MIZUHO BANK LTD',
      editableBranchName: 'KOBE BRANCH',
      editableAccountName: 'IMPERIAL SOLUTIONS LTD',
      editableAccountNo: '1234567',
      editableSwiftCode: 'MHCBJPJT',
      editableShipType: invoice.order?.ship_type || 'RORO',
      editableTradeTerms: invoice.order?.payment_terms || 'CFR',
      editablePaymentTerms: invoice.order?.payment_terms || 'T/T IN ADVANCE',
      editableCAndF: invoice.order?.to_country || '',
      editableShipperPer: invoice.ship_name || '',
      editableVoyageNo: invoice.voyage_no || '',
      editableETD: '',
      editableETA: invoice.arrival_date ? this.formatDate(invoice.arrival_date) : ''
    }));

    // Emit initial data
    this.emitEditableData();
  }

  onFieldChange(): void {
    this.hasChanges = true;
    this.emitEditableData();
  }

  emitEditableData(): void {
    const extraData = this.getEditableFieldsData();
    this.editableDataChange.emit(extraData);
  }

  getEditableFieldsData(): any {
    return this.editableInvoices.map(invoice => ({
      invoice_id: invoice._id,
      // Shipping and port information
      port_of_loading: invoice.editablePortOfLoading,
      port_of_discharging: invoice.editablePortOfDischarging,

      // Bank details
      bank_name: invoice.editableBankName,
      branch_name: invoice.editableBranchName,
      account_name: invoice.editableAccountName,
      account_no: invoice.editableAccountNo,
      swift_code: invoice.editableSwiftCode,

      // Terms and shipping details
      ship_type: invoice.editableShipType,
      trade_terms: invoice.editableTradeTerms,
      payment_terms: invoice.editablePaymentTerms,
      c_and_f: invoice.editableCAndF,
      shipper_per: invoice.editableShipperPer,
      voyage_no: invoice.editableVoyageNo,
      estimated_time_departure: invoice.editableETD,
      estimated_time_arrival: invoice.editableETA,

      // Vehicle data (if edited)
      vehicle_data: invoice.editableVehicle ? {
        make: invoice.editableVehicle.make,
        model: invoice.editableVehicle.model,
        year: invoice.editableVehicle.year,
        body_type: invoice.editableVehicle.body_type,
        engine: invoice.editableVehicle.engine,
        mileage: invoice.editableVehicle.mileage,
        stock_no: invoice.editableVehicle.stock_no,
        fuel_type: invoice.editableVehicle.fuel_type,
        exterior_color: invoice.editableVehicle.exterior_color,
        transmission: invoice.editableVehicle.transmission,
        car_weight: invoice.editableVehicle.car_weight,
        chassis_no: invoice.editableVehicle.chassis_no
      } : null,

      // Multiple vehicles data (for merged invoices)
      vehicles_data: invoice.editableVehicles ? invoice.editableVehicles.map((vehicle: any) => ({
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        chassis_no: vehicle.chassis_no,
        stock_no: vehicle.stock_no,
        engine: vehicle.engine,
        mileage: vehicle.mileage,
        fuel_type: vehicle.fuel_type,
        exterior_color: vehicle.exterior_color,
        transmission: vehicle.transmission,
        invoice_number: vehicle.invoice_number,
        invoice_amount: vehicle.invoice_amount
      })) : null,

      // Invoice amount
      invoice_amount: invoice.editableInvoiceAmount
    }));
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).toUpperCase();
  }

  formatCurrency(amount: number | null, currency: string | null = 'USD'): string {
    if (!amount || amount === 0) return '-';
    const curr = currency || 'USD';
    return `${curr} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // TrackBy functions for performance optimization
  trackByInvoiceIndex(index: number): number {
    return index;
  }

  trackByVehicleIndex(index: number): number {
    return index;
  }
}
